import {useUnderwritingAnalytics} from '@/Core/hooks/useUnderwritingAnalytics';
import {CalcfocusReportRequest} from '@amplify/athena-models';
import {Button, CircularProgress, Grid, Typography} from '@material-ui/core';
import KeyboardBackspaceIcon from '@material-ui/icons/KeyboardBackspace';
import React, {useEffect, useRef, useState} from 'react';
import {AgenuimContextType} from '../../../Core/agenium/ageniumContext';
import {useAgeniumUser} from '../../../Core/hooks/useAgeniumUser';
import {ParameterStore} from '../../../Core/mobx/ParameterStore/ParameterStore';
import {IS_PRODUCTION} from '../../../constants';
import {ContactFormStore} from '../../ContactForm/ContactFormStore/ContactFormStore';
import {getApplicationRequirements, submitDocusign} from '../api/instantIul';
import {useCommonModalStyles} from './common/modalStyles';
import {getAgeniumCalcfocusRequest} from './utils';
interface SendDocusignProps {
    parameterStore: ParameterStore;
    contactFormStore: ContactFormStore;
    onExit: () => void;
    onDocusignSignComplete: () => void;
    ageniumContext: AgenuimContextType;
    onBack?: () => void;
    onDecisionRedirect?: (decision: 'REFERRED' | 'DECLINED' | 'CANCELLED') => void;
}

export const SendDocusign: React.FC<SendDocusignProps> = ({
    parameterStore,
    contactFormStore,
    onBack,
    onExit,
    onDocusignSignComplete,
    ageniumContext,
    onDecisionRedirect,
}) => {
    const [isAwaitingSignature, setIsAwaitingSignature] = useState(false);
    const [isCheckForDocusignCompleteDone, setIsCheckForDocusignCompleteDone] = useState(false);
    const commonClasses = useCommonModalStyles();
    const {ageniumUser} = useAgeniumUser();
    const hasLoadedAppData = useRef(false);
    const {storeDecision} = useUnderwritingAnalytics({
        applicationId: ageniumContext.applicationId,
        formId: parameterStore.formid,
    });

    // Check application decision and redirect if not approved
    useEffect(() => {
        if (
            ageniumContext.applicationDecision &&
            ageniumContext.applicationDecision !== 'APPROVED' &&
            onDecisionRedirect
        ) {
            storeDecision({decision: ageniumContext.applicationDecision});
            onDecisionRedirect(ageniumContext.applicationDecision);
        }
    }, [ageniumContext.applicationDecision, onDecisionRedirect]);

    async function onSendDocusignClick() {
        setIsAwaitingSignature(true);
        const formId = parameterStore.formid as string;
        const instantIulApplicationId = parameterStore.instantIulApplicationId as string;

        const calcfocusReportRequest = getAgeniumCalcfocusRequest(ageniumContext.getApplicationData(), ageniumUser);

        await submitDocusign(
            formId,
            instantIulApplicationId,
            calcfocusReportRequest as unknown as CalcfocusReportRequest,
        );

        pollForDocusignComplete(formId, instantIulApplicationId);
    }

    async function checkForDocusignComplete(formId: string, applicationId: string) {
        const requirements = await getApplicationRequirements(formId, applicationId);
        return !!requirements?.find((r) => r.type === 'policyDocusign' && r.status !== 'outstanding');
    }

    async function pollForDocusignComplete(formId: string, applicationId: string) {
        const poll = setInterval(async () => {
            const hasMetRequirements = await checkForDocusignComplete(formId, applicationId);

            if (hasMetRequirements) {
                setIsAwaitingSignature(false);
                clearInterval(poll);
                parameterStore.eAppDocusignCompleted = new Date();
                await contactFormStore.saveForm();
                onDocusignSignComplete();
            }
        }, 5000);
    }

    useEffect(() => {
        ageniumContext.initSdk();
        // Check once initial page load in case signed when agent was not on page
        checkForDocusignComplete(
            parameterStore.formid as string,
            parameterStore.instantIulApplicationId as string,
        ).then(async (completed) => {
            if (completed) {
                parameterStore.eAppDocusignCompleted = new Date();
                await contactFormStore.saveForm();
                onDocusignSignComplete();
            } else setIsCheckForDocusignCompleteDone(true);
        });
    }, []);

    // This is required to load application data, initSdk is not enough.
    useEffect(() => {
        if (!hasLoadedAppData.current && ageniumContext.applicationNumber && !ageniumContext.applicationData) {
            hasLoadedAppData.current = true;
            ageniumContext.loadApplicationData();
        }
    }, [ageniumContext.applicationNumber, ageniumContext.applicationData]);

    return (
        <Grid container className={commonClasses.wrapper}>
            <Grid item>
                <Typography variant="h4" className={commonClasses.title}>
                    Let’s Send Over The Docusign
                </Typography>
                <Grid container spacing={9}>
                    <Grid item>
                        <Typography className={commonClasses.text}>
                            Just click ‘Send Docusign’ at the bottom to provide a Docusign link to the client via email
                            and SMS
                        </Typography>
                    </Grid>
                    {(!isCheckForDocusignCompleteDone || !ageniumContext.applicationData || isAwaitingSignature) && (
                        <CircularProgress />
                    )}
                </Grid>
            </Grid>
            <Grid container spacing={2} justify="space-around" className={commonClasses.navigation}>
                <Grid item xs={2}>
                    <Button
                        color="primary"
                        variant="text"
                        onClick={onExit}
                        startIcon={<KeyboardBackspaceIcon />}
                        className={commonClasses.backBtn}
                    >
                        Save and exit
                    </Button>
                </Grid>
                {onBack && (
                    <Grid item className={commonClasses.btnWrapper}>
                        <Button color="primary" variant="outlined" onClick={onBack} className={commonClasses.nextBtn}>
                            BACK
                        </Button>
                    </Grid>
                )}
                <Grid item className={commonClasses.btnWrapper}>
                    <Button
                        color="primary"
                        variant="contained"
                        onClick={onSendDocusignClick}
                        className={commonClasses.nextBtn}
                        disabled={
                            !isCheckForDocusignCompleteDone || !ageniumContext.applicationData || isAwaitingSignature
                        }
                    >
                        CONFIRM AND SEND DOCUSIGN
                    </Button>
                </Grid>
            </Grid>
            {!IS_PRODUCTION && (
                <div style={{fontSize: '10px', marginTop: 50}}>
                    <div>applicationData</div>
                    <div style={{color: '#880'}}>{JSON.stringify(ageniumContext.applicationData, null, 2)}</div>
                </div>
            )}
        </Grid>
    );
};
